import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { BalanceData } from "./creditsConfig";

interface Props {
  balance: BalanceData;
  loading: boolean;
  isError: boolean;
  periodEnd: string | null;
  isCompact: boolean;
}

const GLOW_STYLE = {
  background:
    "radial-gradient(ellipse 110% 90% at 50% -10%, hsl(var(--secondary) / 0.14) 0%, hsl(var(--secondary) / 0.05) 50%, transparent 100%)",
} as const;

function PlanProgress({
  planUsed,
  planTotal,
  periodEnd,
}: {
  planUsed: number;
  planTotal: number;
  periodEnd: string | null;
}) {
  const { t } = useTranslation();
  const pct = planTotal > 0 ? Math.min(100, (planUsed / planTotal) * 100) : 0;
  return (
    <div className="mx-auto max-w-[360px] space-y-2">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-secondary [transition:width_400ms_ease]"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex flex-wrap items-baseline justify-center gap-x-1.5 text-[12px]">
        <span className="font-semibold tabular-nums text-foreground">
          {planUsed.toLocaleString()} / {planTotal.toLocaleString()}
        </span>
        <span className="text-muted-foreground">
          {t("payments.balance.planCreditsUsed")}
        </span>
        {periodEnd && (
          <span className="text-muted-2">
            {t("payments.balance.resets", { periodEnd })}
          </span>
        )}
      </div>
    </div>
  );
}

export default function BalanceDisplay({
  balance,
  loading,
  isError,
  periodEnd,
  isCompact,
}: Props) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div
        className={`relative overflow-hidden rounded-xl text-center ${isCompact ? "px-5 py-8" : "px-6 py-11"}`}
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={GLOW_STYLE}
        />
        <div className="rounded mx-auto mb-3.5 h-[11px] w-40 animate-pulse bg-muted" />
        <div
          className={`mx-auto mb-4 animate-pulse rounded-lg bg-muted ${isCompact ? "h-14 w-[200px]" : "h-20 w-[280px]"}`}
        />
        <div className="rounded mx-auto mb-2 h-1.5 max-w-screen-xs animate-pulse bg-muted" />
        <div className="rounded mx-auto h-[11px] w-40 animate-pulse bg-muted" />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className={`relative overflow-hidden rounded-xl text-center ${isCompact ? "px-5 py-8" : "px-6 py-11"}`}
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={GLOW_STYLE}
        />
        <AlertTriangle
          size={22}
          className="mx-auto mb-3 text-muted-foreground"
        />
        <p className="text-[13px] text-muted-foreground">
          {t("payments.balance.unavailable")}
        </p>
      </div>
    );
  }

  const { addon, planUsed, planTotal } = balance;

  if (addon === 0) {
    return (
      <div
        className={`relative overflow-hidden rounded-xl text-center ${isCompact ? "px-5 py-6" : "px-7 py-9"}`}
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={GLOW_STYLE}
        />
        <p className="mb-2.5 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-secondary">
          {t("payments.balance.addonCreditsLabel")}
        </p>
        <h2
          className={`mb-2.5 font-semibold leading-snug tracking-tight text-foreground ${isCompact ? "text-xl" : "text-2xl"}`}
        >
          {t("payments.balance.topUpTitle")}
        </h2>
        <p
          className={`mx-auto mb-4 leading-relaxed text-muted-foreground ${isCompact ? "max-w-[360px] text-[13px]" : "max-w-[480px] text-[14px]"}`}
        >
          {t("payments.balance.topUpBody")}
        </p>
        <PlanProgress
          planUsed={planUsed}
          planTotal={planTotal}
          periodEnd={periodEnd}
        />
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-xl text-center ${isCompact ? "px-5 py-[22px]" : "px-6 py-9"}`}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={GLOW_STYLE}
      />
      <p className="mb-2.5 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-secondary">
        {t("payments.balance.addonCreditsAvailable")}
      </p>
      <div className="mb-4 flex flex-col items-center justify-center gap-0.5">
        <span
          className={`font-bold tabular-nums leading-none tracking-tight text-foreground ${isCompact ? "text-[44px]" : "text-[60px]"}`}
        >
          {addon.toLocaleString()}
        </span>
        <span
          className={`font-medium text-muted-foreground ${isCompact ? "text-sm" : "text-base"}`}
        >
          {addon === 1
            ? t("payments.balance.credit")
            : t("payments.balance.credits")}
        </span>
      </div>
      <PlanProgress
        planUsed={planUsed}
        planTotal={planTotal}
        periodEnd={periodEnd}
      />
    </div>
  );
}
